import React from "react";
import styles from "./Footer.module.css";
import { useT } from "../../i18n";

const PHONE_DISPLAY = "(480) 282-0569";
const PHONE_TEL = "+14802820569";

// Credly badge verify links. Confirm each ID resolves to the right badge:
// https://www.credly.com/badges/{id}/public_url
const CREDENTIALS = [
  {
    id: "f3d1e247-4f44-43e3-8d81-7d44cf6e07f5",
    name: "Microsoft Certified: Power Platform Fundamentals",
    image: "/credly/microsoft-certified-power-platform-fundamentals.png",
  },
  {
    id: "c981bacf-9d4b-4dcf-8cc7-e4e1d27b18a2",
    name: "Microsoft Certified: Dynamics 365 Fundamentals (CRM)",
    image: "/credly/microsoft-certified-dynamics-365-fundamentals-crm.png",
  },
  {
    id: "d1194b11-9cdb-48d0-bfa8-596a7a4f7e22",
    name: "Catalyte: Full Stack Developer (Java/React)",
    image: "/credly/full-stack-java-react.png",
  },
  {
    id: "ed7333ea-ff9d-45b1-bb7c-138952f44d94",
    name: "Catalyte: AI Foundational Skills",
    image: "/credly/ai-foundational-skills.png",
  },
];

// City names are proper nouns — same in both locales.
const AREAS = ["Goodyear", "Avondale", "Peoria", "Surprise", "Scottsdale", "Phoenix"];

const Footer = () => {
  const t = useT().footer;

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <div className={styles.top}>
          <a href="/" className={styles.brandLink} aria-label="Exhale Software Solutions, home">
            <img
              src="/Exhale.Daniel.Ray.Rangel.png"
              alt=""
              className={styles.brandLogo}
            />
            <span className={styles.brandText}>
              <span className={styles.brandName}>Exhale Software Solutions</span>
              <span className={styles.brandTagline}>{t.tagline}</span>
            </span>
          </a>
        </div>

        <div className={styles.columns}>
          <div className={styles.column}>
            <p className={styles.columnTitle}>{t.buildTitle}</p>
            <ul className={styles.list}>
              {t.services.map((s) => (
                <li key={s}>{s}</li>
              ))}
              <li>
                <a href="/tools/qr" className={styles.toolLink}>
                  Free QR Code Generator
                  <span className={styles.toolTag}>Free</span>
                </a>
              </li>
              <li>
                <a href="/tools/tip-split" className={styles.toolLink}>
                  Free Tip Split Calculator
                  <span className={styles.toolTag}>Free</span>
                </a>
              </li>
              {/* The hub goes under the two it collects, not above them: the
                  direct links are what people follow, and they carry the tool
                  names a search engine is looking for. */}
              <li>
                <a href="/tools" className={styles.toolLink}>
                  All free tools
                  <span className={styles.toolTag}>Hub</span>
                </a>
              </li>
            </ul>
          </div>

          <div className={styles.column}>
            <p className={styles.columnTitle}>{t.areasTitle}</p>
            <ul className={styles.list}>
              {AREAS.map((a) => (
                <li key={a}>{a}, AZ</li>
              ))}
              <li className={styles.faint}>{t.remote}</li>
            </ul>
          </div>

          <div className={styles.column}>
            <p className={styles.columnTitle}>{t.connectTitle}</p>
            <ul className={styles.list}>
              <li>
                <a href={`tel:${PHONE_TEL}`} className={styles.callLink}>
                  {t.call} {PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a href={`sms:${PHONE_TEL}`}>
                  {t.text}
                </a>
              </li>
              <li>
                <a href="mailto:daniel@danielrayrangel.com">
                  daniel@danielrayrangel.com
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/in/danielrayrangel/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
              </li>
              <li>
                <a
                  href="https://www.facebook.com/exhalesoftware/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Facebook
                </a>
              </li>
              <li className={styles.faint}>{t.hours}</li>
            </ul>
          </div>
        </div>

        <div className={styles.credentials}>
          <div className={styles.credHeader}>
            <span className={styles.credRule} aria-hidden="true" />
            <span className={styles.credLabel}>{t.credTitle}</span>
            <span className={styles.credRule} aria-hidden="true" />
          </div>
          <ul className={styles.credList}>
            {CREDENTIALS.map((cred) => (
              <li key={cred.id}>
                <a
                  href={`https://www.credly.com/badges/${cred.id}/public_url`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.credLink}
                  title={`Verify on Credly: ${cred.name}`}
                  aria-label={`${cred.name}, verify on Credly`}
                >
                  <img
                    src={cred.image}
                    alt={cred.name}
                    className={styles.credImage}
                    loading="lazy"
                    width="84"
                    height="84"
                  />
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.bottom}>
          <p className={styles.copyright}>
            &copy; 2026 Exhale Software Solutions &middot;{" "}
            <a
              href="https://www.linkedin.com/in/danielrayrangel/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.nameLink}
            >
              Daniel Rangel
            </a>
            . {t.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
